import { CloseOutlined, QuestionCircleOutlined, UploadOutlined } from '@ant-design/icons'
import { Button, Col, Form, Row, Tooltip, Upload } from 'antd'
import { isEqual } from 'lodash'
import { IBaseFormProps } from 'Modules'
import { useEffect, useState } from 'react'

import { ALLOWED_MIME_TYPES_IMAGE } from 'constants/mime-type'
import usePrevious from 'hooks/use-previous'
import useUploadFile from 'hooks/use-upload-file'

interface IUploadFileProps extends IBaseFormProps {
  defaultThumbnail?: string
  defaultThumbnails?: string[]
  onUpload?: (data: any) => void
  onUploadMulti?: (data: any) => void
}

const UploadFile = ({
  edit = true,
  form,
  defaultThumbnail,
  defaultThumbnails,
  onUpload,
  onUploadMulti,
}: IUploadFileProps) => {
  const [thumbnail, setThumbnail] = useState<string>('')
  const [thumbnails, setThumbnails] = useState<string[]>([])

  const previewThumbnail = usePrevious(defaultThumbnail)
  const previewThumbnails = usePrevious(defaultThumbnails)

  useEffect(() => {
    if (defaultThumbnail && !isEqual(previewThumbnail, defaultThumbnail)) {
      setThumbnail(defaultThumbnail)
    }
  }, [defaultThumbnail, previewThumbnail])

  useEffect(() => {
    if (defaultThumbnails && !isEqual(defaultThumbnails, previewThumbnails)) {
      setThumbnails(defaultThumbnails)
    }
  }, [defaultThumbnails, previewThumbnails])

  const {
    singleFile,
    fileList,
    handleUploadSingle,
    handleUploadMulti,
    handleRemoveMulti,
    beforeUploadSingle,
    beforeUploadMulti,
    handleRemoveFile,
  } = useUploadFile({
    onUploadSuccess: (data) => {
      //setThumbnail(data.filePath)
      onUpload?.(data.filePath)
      form.setFields([{ name: 'uploadSingle', errors: [] }])
    },
    onUploadMultiSuccess: (data) => {
      const list = data.map((item: any) => item.filePath)
      const updatedList = [...thumbnails, ...list]
      //setThumbnails(updatedList)
      onUploadMulti?.(updatedList)
      form.setFields([{ name: 'uploadMulti', errors: [] }])
    },
    onBeforeUpload: () => {
      form.setFields([{ name: 'uploadSingle', errors: ['You can only upload JPG/PNG file!'] }])
    },
    onBeforeUploadMulti: () => {
      form.setFields([{ name: 'upLloadMulti', errors: ['You can only upload JPG/PNG file!'] }])
    },
  })

  const handleRemoveImage = (indexToRemove: any) => {
    const updatedThumbnails = thumbnails.filter((_, index) => index !== indexToRemove)
    //setThumbnails(updatedThumbnails)
    onUploadMulti?.(updatedThumbnails)
    // Ensure this updates your thumbnails state
  }

  return (
    <fieldset>
      <legend>Upload Picture</legend>
      <Row gutter={24}>
        <Col style={{ position: 'relative' }} span={12}>
          {thumbnail.length > 0 && (
            <>
              <img style={{ width: '50%', border: '1px solid' }} src={thumbnail} alt="" />
              <CloseOutlined
                style={{
                  position: 'absolute',
                  top: 4,
                  right: '50%',
                  color: '#000',
                  fontSize: '16px',
                  cursor: 'pointer',
                  background: 'white',
                  borderRadius: '50%',
                  padding: '2px',
                  border: '1px solid',
                  display: edit ? 'unset' : 'none',
                }}
                onClick={() => {
                  setThumbnail('')
                  handleRemoveFile()
                }}
              />
            </>
          )}
        </Col>
        <Col span={12}>
          <Row gutter={24}>
            {thumbnails.length > 0 &&
              thumbnails.map((item, index) => (
                <Col span={8} key={index} style={{ position: 'relative' }}>
                  <img style={{ width: '100%', border: '1px solid' }} src={item} alt="" />
                  <CloseOutlined
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 14,
                      color: '#000',
                      fontSize: '16px',
                      cursor: 'pointer',
                      background: 'white',
                      borderRadius: '50%',
                      padding: '2px',
                      border: '1px solid',
                      display: edit ? 'unset' : 'none',
                    }}
                    onClick={() => handleRemoveImage(index)}
                  />
                </Col>
              ))}
          </Row>
        </Col>
        {edit && (
          <>
            <Col style={{ marginBottom: 20, marginTop: 20 }} span={12}>
              <div style={{ marginBottom: 10 }}>
                <span style={{ color: 'red' }}>*</span> Upload new avatar{' '}
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your PTO avatar. Please upload your chosen image.
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </div>
              <Form.Item name="uploadSingle">
                <Upload
                  disabled={!edit}
                  fileList={singleFile}
                  customRequest={handleUploadSingle}
                  beforeUpload={beforeUploadSingle}
                  accept={ALLOWED_MIME_TYPES_IMAGE.join(',').toString()}
                >
                  <Button icon={<UploadOutlined />}>Upload Single File</Button>
                </Upload>
              </Form.Item>
              <p>You can only upload JPG/PNG file.</p>
              <p>The image should have the size of 361x176 px.</p>
            </Col>
            <Col style={{ marginBottom: 20, marginTop: 20 }} span={12}>
              <div style={{ marginBottom: 10 }}>
                <span style={{ color: 'red' }}>*</span> Upload new thumbnails{' '}
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your PTO background thumbnails list. Please upload your chosen
                        images.
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </div>
              <Form.Item name="upLloadMulti">
                <Upload
                  listType="text"
                  beforeUpload={beforeUploadMulti}
                  onRemove={handleRemoveMulti}
                  fileList={fileList}
                  disabled={!edit}
                  accept={ALLOWED_MIME_TYPES_IMAGE.join(',').toString()}
                >
                  <Button icon={<UploadOutlined />}>Upload Multiple Files</Button>
                </Upload>
                <Button
                  type="primary"
                  onClick={handleUploadMulti}
                  disabled={fileList.length === 0}
                  style={{ marginTop: 16 }}
                >
                  Start Upload
                </Button>
              </Form.Item>

              <p>You can only upload JPG/PNG file.</p>
              <p>The image should have the size of 1071x451 px.</p>
            </Col>
          </>
        )}
      </Row>
    </fieldset>
  )
}

export default UploadFile
